import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// QB Mapping:
// estimate.estimate_number → DocNumber
// estimate.customer_id → CustomerRef.value
// estimate.txn_date → TxnDate
// estimate.expiry_date → ExpiryDate
// estimate.notes → CustomerMemo
// estimate.total_amount → TotalAmt
//
// Each SubLine flattens to one QB SalesItemLineDetail:
//   SubLine.description → Line[].Description
//   SubLine.qty → Line[].SalesItemLineDetail.Qty
//   SubLine.unit_price → Line[].SalesItemLineDetail.UnitPrice (billed price)
//   SubLine.qb_tax_code → Line[].SalesItemLineDetail.TaxCodeRef ('NON' for labor, 'TAX' for material)
//   SubLine.qb_item_ref → Line[].SalesItemLineDetail.ItemRef ('Installation Labor' | 'Job Materials')

export interface SubLine {
  id: string;
  type: "labor" | "material";
  description: string;
  qty: number;
  unit: string;
  cost_price: number;       // true cost — used for WO/PO assignment
  markup_pct: number;       // default 35
  unit_price: number;       // cost × (1 + markup/100) — billed to customer
  total_cost: number;       // qty × cost_price
  total_billed: number;     // qty × unit_price
  taxable: boolean;         // false for labor, true (default) for material
  qb_tax_code: "NON" | "TAX";
  qb_item_ref: string;
  product_service_id?: string;
  income_account?: string;
  cogs_account?: string;
  // backward compat: old estimates may have `total` instead of `total_billed`
  total?: number;
}

export interface LineItem {
  line_num: number;
  description: string;
  customer_description: string;  // merged description for customer view
  qty: number;
  unit: string;
  sub_lines: SubLine[];
  labor_total_cost: number;
  labor_total_billed: number;
  material_total_cost: number;
  material_total_billed: number;
  total_cost: number;
  total_billed: number;
  // backward compat
  labor_total?: number;
  material_total?: number;
  total?: number;
}

// Legacy flat format (backward compat for old estimates)
export interface LegacyLineItem {
  description: string;
  line_type: "labor" | "material" | "both";
  qty: number;
  unit: string;
  unit_price: number;
  labor_price: number;
  material_price: number;
  total: number;
}

export type JobType = 'flooring' | 'roofing' | 'emergency' | 'general' | 'other';

export interface Estimate {
  id: string;
  company_id: string;
  estimate_number: string | null;
  customer_id: string | null;
  txn_date: string;
  expiry_date: string | null;
  status: string;
  job_type: JobType;
  line_items: LineItem[] | LegacyLineItem[];
  subtotal: number;
  total_amount: number;
  notes: string | null;
  qb_estimate_id: string | null;
  qb_sync_status: string | null;
  qb_synced_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  customers?: { name: string } | null;
}

export function isNewFormat(item: any): item is LineItem {
  return Array.isArray(item?.sub_lines);
}

/** Get the billed total from a line item (handles old and new format) */
function getLineBilled(li: LineItem): number {
  return li.total_billed ?? li.total ?? 0;
}

/** Derive sov_line fields from a LineItem for contract creation */
export function lineItemToSovFields(li: LineItem) {
  const qty = li.qty || 1;
  const laborBilled = li.labor_total_billed ?? li.labor_total ?? 0;
  const materialBilled = li.material_total_billed ?? li.material_total ?? 0;
  const totalBilled = li.total_billed ?? li.total ?? 0;
  const totalCost = li.total_cost ?? 0;
  const laborUp = laborBilled / qty;
  const materialUp = materialBilled / qty;
  const hasLabor = li.sub_lines.some((sl) => sl.type === "labor");
  const hasMaterial = li.sub_lines.some((sl) => sl.type === "material");
  const lineType = hasLabor && hasMaterial ? "both" : hasLabor ? "labor" : "materials";

  return {
    description: li.description,
    quantity: qty,
    unit: li.unit,
    labor_unit_price: laborUp,
    material_unit_price: materialUp,
    unit_price: laborUp + materialUp,
    scheduled_value: totalBilled,
    line_type: lineType,
    sub_lines: li.sub_lines,
    total_cost: totalCost,
    total_billed: totalBilled,
  };
}

/** Generate a crypto UUID (browser-safe) */
function uuid(): string {
  return crypto.randomUUID();
}

/** Build a SubLine with defaults. Accepts optional parent context for auto-fill. */
export function makeSubLine(
  type: "labor" | "material",
  partial?: Partial<SubLine>,
  parentContext?: { qty?: number; unit?: string; description?: string }
): SubLine {
  const cost_price = partial?.cost_price ?? 0;
  const markup_pct = partial?.markup_pct ?? (type === "labor" ? 35 : 20);
  const unit_price = partial?.unit_price ?? (cost_price > 0 ? cost_price * (1 + markup_pct / 100) : 0);
  const qty = partial?.qty ?? parentContext?.qty ?? 1;
  const unit = partial?.unit ?? parentContext?.unit ?? "SF";
  const taxable = partial?.taxable ?? (type === "material");
  const desc = partial?.description ?? (parentContext?.description
    ? (type === "labor" ? `${parentContext.description} - Labor` : parentContext.description)
    : "");
  return {
    id: uuid(),
    type,
    description: desc,
    qty,
    unit,
    cost_price,
    markup_pct,
    unit_price,
    total_cost: qty * cost_price,
    total_billed: qty * unit_price,
    taxable,
    qb_tax_code: taxable ? "TAX" : "NON",
    qb_item_ref: type === "labor" ? "Installation Labor" : "Job Materials",
    ...partial,
  };
}

/** Build a LineItem with defaults */
export function makeLineItem(lineNum: number): LineItem {
  return {
    line_num: lineNum,
    description: "",
    customer_description: "",
    qty: 1,
    unit: "SF",
    sub_lines: [makeSubLine("labor")],
    labor_total_cost: 0,
    labor_total_billed: 0,
    material_total_cost: 0,
    material_total_billed: 0,
    total_cost: 0,
    total_billed: 0,
  };
}

/** Recalculate LineItem totals from its sub_lines */
export function recalcLineItem(li: LineItem): LineItem {
  let laborTotalCost = 0;
  let laborTotalBilled = 0;
  let materialTotalCost = 0;
  let materialTotalBilled = 0;

  const sub_lines = li.sub_lines.map((sl) => {
    const total_cost = sl.qty * sl.cost_price;
    const total_billed = sl.qty * sl.unit_price;
    if (sl.type === "labor") {
      laborTotalCost += total_cost;
      laborTotalBilled += total_billed;
    } else {
      materialTotalCost += total_cost;
      materialTotalBilled += total_billed;
    }
    return { ...sl, total_cost, total_billed, total: total_billed };
  });

  return {
    ...li,
    sub_lines,
    labor_total_cost: laborTotalCost,
    labor_total_billed: laborTotalBilled,
    material_total_cost: materialTotalCost,
    material_total_billed: materialTotalBilled,
    total_cost: laborTotalCost + materialTotalCost,
    total_billed: laborTotalBilled + materialTotalBilled,
    // backward compat
    labor_total: laborTotalBilled,
    material_total: materialTotalBilled,
    total: laborTotalBilled + materialTotalBilled,
  };
}

export function useEstimates() {
  return useQuery<Estimate[]>({
    queryKey: ["estimates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estimates")
        .select("*, customers(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Estimate[];
    },
  });
}

interface CreateEstimateInput {
  customer_id: string | null;
  txn_date: string;
  expiry_date: string | null;
  notes: string | null;
  line_items: LineItem[];
  subtotal: number;
  total_amount: number;
  qb_sync_status: string;
  job_type?: JobType;
}

export function useCreateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateEstimateInput) => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      const { data, error } = await supabase
        .from("estimates")
        .insert({
          company_id: companyId,
          customer_id: input.customer_id || null,
          txn_date: input.txn_date,
          expiry_date: input.expiry_date || null,
          notes: input.notes || null,
          line_items: input.line_items as any,
          subtotal: input.subtotal,
          total_amount: input.total_amount,
          qb_sync_status: input.qb_sync_status,
          job_type: input.job_type || 'general',
        })
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["estimates"] });
    },
  });
}

export function useUpdateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<CreateEstimateInput> & { id: string; status?: string }) => {
      const updateObj: Record<string, any> = {};
      if (input.customer_id !== undefined) updateObj.customer_id = input.customer_id || null;
      if (input.txn_date !== undefined) updateObj.txn_date = input.txn_date;
      if (input.expiry_date !== undefined) updateObj.expiry_date = input.expiry_date || null;
      if (input.notes !== undefined) updateObj.notes = input.notes || null;
      if (input.line_items !== undefined) updateObj.line_items = input.line_items as any;
      if (input.subtotal !== undefined) updateObj.subtotal = input.subtotal;
      if (input.total_amount !== undefined) updateObj.total_amount = input.total_amount;
      if (input.qb_sync_status !== undefined) updateObj.qb_sync_status = input.qb_sync_status;
      if (input.job_type !== undefined) updateObj.job_type = input.job_type;
      if ((input as any).status !== undefined) updateObj.status = (input as any).status;

      const { data, error } = await supabase
        .from("estimates")
        .update(updateObj)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["estimates"] });
    },
  });
}
